import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import Dropdown from 'react-dropdown'
import IconSelectArrow from '@/svgs/select-arrow.svg'
import classes from './ProductEarlybirdForm.module.scss'
import LoadingState from '@/components/LoadingState'
import { getMetafield } from '@/utils/getMetafield'
import { useHeadlessCheckoutContext } from '@/context/HeadlessCheckoutContext'
import ProductPricing from '../ProductPricing'
import { useCustomerContext } from '@/context/CustomerContext'

function buildSelectors(allProducts) {
  return allProducts.reduce((carry, product) => {
    const productHandle = product.content?.handle
    const selectorDetailsMetafield = getMetafield({
      product,
      key: 'selector_details',
    })

    const productObj = {
      product,
      boxOption: {
        value: productHandle,
        label: (
          <div
            className={
              classes['subscription-selector__product-selector-details']
            }
          >
            <span>{product.content?.title}</span>{' '}
            {selectorDetailsMetafield && (
              <span> • {selectorDetailsMetafield.value}</span>
            )}{' '}
          </div>
        ),
      },
      frequencyOptions: [],
      shipmentsOptions: [],
    }

    productObj.frequencyOptions.push('Monthly')
    productObj.frequencyOptions.push('Every Other Month')

    productObj.shipmentsOptions.push('6')
    productObj.shipmentsOptions.push('12')

    carry[productHandle] = productObj
    return carry
  }, {})
}

const ShipmentOptions = {
  Monthly: '6',
  monthly: '6',
  EveryOtherMonth: '12',
  'Every Other Month': '12',
}

const ProductEarlybirdForm = ({ allProducts, product }) => {
  const router = useRouter()

  const { addItemToOrder } = useHeadlessCheckoutContext()
  const { customer } = useCustomerContext()
  const selectors = buildSelectors(allProducts)
  const [selectedVariant, setSelectedVariant] = useState(product.variants[0])
  const [frequencySelected, setFrequencySelected] = useState(
    selectors[selectedVariant.productHandle].frequencyOptions[0],
  )
  const [shipmentSelected, setShipmentSelected] = useState(
    selectors[selectedVariant.productHandle].shipmentsOptions[0],
  )
  const [isLoading, setIsLoading] = useState(false)
  const [disableDropdown, setDisableDropdown] = useState(false)
  const [subscriptionId, setSubscriptionId] = useState(0)
  const [lineItemId, setLineItemId] = useState(0)

  function getDiscountBySustainerType(sustainerType, price) {
    switch (sustainerType) {
      case 'SockeyeSustainer':
        return price * 0.85
      case 'KingSustainer':
        return price * 0.8
      default:
        return price * 1
    }
  }

  const totalPrice = selectedVariant.price * parseInt(shipmentSelected)

  const sustainerTags = customer?.tags?.find(
    (tag) => tag === 'SockeyeSustainer' || tag === 'KingSustainer',
  )

  const discountedPrice = customer?.is_sustainer
    ? getDiscountBySustainerType(sustainerTags, totalPrice)
    : totalPrice * 0.9

  let options = null
  if (product?.content?.options?.some((option) => option.values.length > 1)) {
    options = product?.content?.options[0].values
  }

  const buttonText = selectedVariant.availableForSale
    ? 'Add To Cart'
    : 'Coming Soon'

  const handleAddItem = async (event) => {
    event.preventDefault()
    setIsLoading(true)

    await addItemToOrder({
      variant: selectedVariant,
      quantity: parseInt(shipmentSelected),
      properties: {
        frequency: frequencySelected.toString(),
        shipments: shipmentSelected.toString(),
        subId: subscriptionId.toString(),
        lineItemId: lineItemId.toString(),
        membership_type: 'earlybird',
      },
      product,
      redirectToCart: true,
    })

    setIsLoading(false)
  }

  const onSelectVariant = (value) => {
    const variant = product.variants.find(
      (variant) =>
        variant.content?.title === value.label.replace('  ', ' ') ||
        variant.sku === value.label.replace('  ', ' '),
    )
    if (variant) setSelectedVariant(variant)
  }

  useEffect(() => {
    if (router.isReady && !router.query.f) {
      setShipmentSelected(
        selectors[selectedVariant.productHandle].shipmentsOptions[0],
      )
    }
  }, [product, router.query, router.isReady])

  useEffect(() => {
    // if frequency comes from query param, do not update it
    if (router.isReady && !router.query.f) {
      setFrequencySelected(
        selectors[selectedVariant.productHandle].frequencyOptions[0],
      )
    }
  }, [product, router.query, router.isReady])

  useEffect(() => {
    if (router.isReady && 
      typeof window !== 'undefined' &&
      navigator.cookieEnabled &&
      router.query.p
    ) {
      if (router.query.p) {
        onSelectVariant({ label: router.query.p })
        setDisableDropdown(true)
      }

      if (router.query.f) {
        let frequecyParam = router.query.f
        let shipmentValue = null

        if (ShipmentOptions[frequecyParam]) {
          shipmentValue = ShipmentOptions[frequecyParam]
        } else {
          shipmentValue = ShipmentOptions.Monthly
        }

        setShipmentSelected(shipmentValue)
      }

      if (router.query.s) {
        setShipmentSelected(router.query.s)
      }

      if (router.query.subId) {
        setSubscriptionId(router.query.subId)
      }

      if (router.query.lineItemId) {
        setLineItemId(router.query.lineItemId)
      }
    }
  }, [router.query, router.isReady])

  return (
    <>
      <div className={classes['prices']}>
        <ProductPricing
          product={product}
          selectedVariant={selectedVariant}
          mainPriceOverride={discountedPrice}
          compareAtPriceOverride={totalPrice}
          large
        />
      </div>
      <form
        onSubmit={(e) => handleAddItem(e)}
        className={classes['product-gift-subscription-form']}
      >
        <ul className={classes['product-subscription-selector']}>
          {/* Box Option */}
          <li
            className={`${classes['product-subscription-selector__item']} ${classes['flex-column']}`}
          >
            <h2 className="heading__pdp-label">
              <span
                className={classes['product-subscription-selector__item-step']}
              >
                1
              </span>{' '}
              Subscription
            </h2>
            <Dropdown
              disabled={disableDropdown}
              className={`dropdown-selector`}
              options={options ? options.map((option) => option) : []}
              onChange={(e) => onSelectVariant(e)}
              value={selectedVariant.content?.title}
              arrowClosed={
                <IconSelectArrow className="dropdown-selector__arrow-closed" />
              }
              arrowOpen={
                <IconSelectArrow className="dropdown-selector__arrow-open" />
              }
            />
          </li>

          {/* Shipments */}
          <li
            className={`${classes['product-subscription-selector__item']} ${classes['product-subscription-selector__shipment']}`}
          >
            <h2 className="heading__pdp-label">
              <span
                className={classes['product-subscription-selector__item-step']}
              >
                2
              </span>{' '}
              Shipments
            </h2>
            <ul>
              {selectors[selectedVariant.productHandle].shipmentsOptions.map(
                (option, index) => {
                  return (
                    <li
                      className={
                        classes['subscription-selector__shipment-item']
                      }
                      key={index}
                    >
                      <button
                        type="button"
                        onClick={() => {
                          setShipmentSelected(option)
                        }}
                        className={`${classes['subscription-selector__shipment-item-btn']} btn ${shipmentSelected === option ? 'sitkablue' : 'pureWhite'}`}
                      >
                        <span>{option}</span>
                      </button>
                    </li>
                  )
                },
              )}
            </ul>
          </li>
        </ul>

        {/* <button type="submit" disabled={!selectedVariant.availableForSale || selectedVariant.quantity === 0 || selectedVariant.sku?.includes('AO-') || selectedVariant.sku?.includes('SWP-') || isLoading} className="btn salmon"> */}
        <button
          type="submit"
          disabled={!selectedVariant.availableForSale || isLoading}
          className="btn salmon"
        >
          {isLoading ? <LoadingState /> : buttonText}
        </button>
      </form>
    </>
  )
}

export default ProductEarlybirdForm
